import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './MultiSelect.scss'; // Reuse styles from GroupedMultiSelect
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SelectedChipList from './SelectedChipList.jsx';

const SimpleMultiSelect = ({ options = [], selectedValues = [], onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const handleOptionToggle = (value) => {
        const newSelectedValues = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value];
        onChange(newSelectedValues);
    };

    const handleSelectAllToggle = () => {
        const allSelected = options.every((option) => selectedValues.includes(option.value));
        const newSelectedValues = allSelected
            ? []
            : options.map((option) => option.value);
        onChange(newSelectedValues);
    };

    const getDisplayText = () => {
        if (!selectedValues || !Array.isArray(selectedValues)) {
            return `Select option`;
        }
        if (selectedValues.length === 0) {
            return `Select option`;
        }
        if (selectedValues.length === 1) {
            return `1 option selected`;
        }
        return `${selectedValues.length} options selected`;
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !event.target.closest('.multi-select') // Check if the click is inside another multi-select
            ) {
                setIsOpen(false);
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        }; // Added missing semicolon
    }, []);

    return (
        <div>
            <div className="simple-multi-select multi-select" ref={dropdownRef}>
                <div className="multi-select-trigger" onClick={() => setIsOpen(!isOpen)}>
                    <span className="multi-select-value">{getDisplayText()}</span>
                    <span className="multi-select-arrow">{isOpen ? '▲' : '▼'}</span>
                </div>
                {isOpen && (
                    <div className="dropdown-menu">
                        <div className="select-all-items">
                            <input
                                type="checkbox"
                                checked={options.every((option) => selectedValues.includes(option.value))}
                                onChange={handleSelectAllToggle}
                            />
                            <label>Select All Items</label>
                        </div>
                        <div className="select-options">

                            {options.map((option) => (
                                <div key={option.value} className="option">
                                    <label data-testid={`activity-option-${option.value}`}>
                                        <input
                                            type="checkbox"
                                            checked={selectedValues.includes(option.value)}
                                            onChange={() => handleOptionToggle(option.value)}
                                        />
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <SelectedChipList
                items={selectedValues
                    .map((value) => {
                        const option = options.find((opt) => opt.value === value);
                        return option ? { value, label: option.label } : null;
                    })
                    .filter(Boolean)}
                onRemove={handleOptionToggle}
            />
        </div>
    );
};

SimpleMultiSelect.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    selectedValues: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
};

export default SimpleMultiSelect;